import React from 'react'
import { Card, TitleText, SubtitleText, BodyText } from '@/components/ui'
import { Layout } from '../layout/Layout'
import { useTheme } from '@/hooks/useTheme'
import { cn } from '@/utils/cn'

export interface TermsAndConditionsPageProps {
  className?: string
}

const TermsAndConditionsPage: React.FC<TermsAndConditionsPageProps> = ({ className }) => {
  const { isDark } = useTheme();

  return (
    <Layout className={className}>
      <div className="min-h-screen pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 mt-16">
            <TitleText>
              BITEWISE MEDICAL TERMS AND CONDITIONS
            </TitleText>
            <BodyText variant="secondary" size="lg">
              These Terms and Conditions (<b>T&Cs</b>) govern the relationship between Biz Group Pty Ltd ACN 671 345
              181 trading as BiteWise Medical (<b>us</b>, <b>we</b> and <b>our</b>) and each medical practitioner, dietitian, patient
              and user (<b>you</b> and <b>your</b>) in respect of your use of BiteWise Medical (<b>Application</b>).
            </BodyText>
          </div>

          {/* Content */}
          <Card className="p-8 space-y-8">
            {/* Introduction */}
            <section>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  We may vary the T&Cs from time to time with fourteen (14) days' notice via email. At the end of that
                  fourteen (14) day period the changes will become binding upon you. If you continue dealings with us
                  following expiry of the fourteen (14) day period you will be deemed to have accepted the T&Cs, as
                  amended from time to time.
                </p>
                <p>
                  The T&Cs create a legally binding agreement between us and you. In all applicable circumstances,
                  you irrevocably waive any rights you may have to appeal, review, or otherwise oppose any
                  determinations or actions of ours in relation to the Application or other related matters.
                </p>
              </div>
            </section>

            {/* Section A - Application */}
            <section>
              <SubtitleText>
                A. Application
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  By using the Application, you irrevocably acknowledge and agree:
                </p>
              </div>
            </section>

            {/* 1. Compliance */}
            <section>
              <SubtitleText>
                1. Compliance
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  1.1 To comply with the T&Cs and any policies published by us from time to time.
                </p>
              </div>
            </section>

            {/* 2. Intellectual Property Rights */}
            <section>
              <SubtitleText>
                2. Intellectual Property Rights
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  2.1 Not to copy, use, communicate to the public, or transmit the content of the Application,
                  including logos, images and any dietary or meal plan, without our prior written consent unless
                  such use is a fair dealing under the <em>Copyright Act 1968</em> (Cth).
                </p>
                <p>
                  2.2 That the software, code, text, data and metadata of the Application is owned by us. You agree
                  not to copy, download for use, reverse engineer or otherwise attempt to capture the
                  software, code, design, data, metadata, operation, system or processes used on the
                  Application.
                </p>
                <p>
                  2.3 To obtain our prior written permission before reproducing any of the content on the
                  Application. When we approve the reproduction of any Application content, it must be
                  attributed to us.
                </p>
                <p>
                  2.4 That we own the copyright in any dietary or meal plan produced via the Application, and that
                  we provide you with:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) if you are a patient, a non-exclusive, perpetual, royalty-free licence to use the dietary or
                      meal plan; and</li>
                  <li>(b) if you are a Subscriber, a non-exclusive, royalty-free licence to use the dietary or meal
                      plan while you are a Subscriber.</li>
                </ul>
              </div>
            </section>

            {/* 3. Use of Application */}
            <section>
              <SubtitleText>
                3. Use of Application
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  3.1 Not to do anything that may disrupt the use of the Application or any other third party
                  software, devices, services or other things necessary to the operation of the Application, and
                  specifically not to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) data mine or conduct automated searches on the Application or its content, whether
                      through the use of additional software or otherwise;</li>
                  <li>(b) incorporate or use any of the content on the Application, including advertising or
                      promotional materials;</li>
                  <li>(c) frame or mirror the Application;</li>
                  <li>(d) tamper with, hinder the operation of, or make unauthorised modifications to the
                      Application or any content on the Application;</li>
                  <li>(e) knowingly transmit any virus, worm or other disabling feature to or via the Application;</li>
                  <li>(f) advertise or offer to sell any goods or services, or conduct or forward surveys, contests,
                      or chain letters from the Application;</li>
                  <li>(g) delete any author attributions, legal notices or proprietary designations or labels in any
                      file that is uploaded;</li>
                  <li>(h) use the Application to send commercial, unsolicited or bulk electronic messages to
                      anyone or in any other way which would constitute an infringement of the <em>Spam Act
                      2003</em> (Cth); or</li>
                  <li>(i) use the Application or any facilities available on the Application for any activities, or
                      transmit to or via the Application any information or content which:</li>
                </ul>
                <ul className="list-disc pl-12 space-y-2">
                  <li>(i) breaches any laws or regulations;</li>
                  <li>(ii) breaches these T&Cs;</li>
                  <li>(iii) infringes a third party's rights (including intellectual property rights, rights of
                      privacy or their trade secrets);</li>
                  <li>(iv) is inappropriate, offensive, obscene, threatening, indecent, inflammatory,
                      pornographic, defamatory or confidential;</li>
                  <li>(v) is false or misleading;</li>
                  <li>(vi) is discriminatory or in breach of State or Commonwealth anti-discrimination
                      legislation; or</li>
                  <li>(vii) identifies a person, or which can be used to identify a person (including any copy,
                      photos or other pictorial representations), unless you have obtained that
                      person's prior written authority; or</li>
                  <li>(viii) attempt to do, or permit another person to do, any of the above acts.</li>
                </ul>
              </div>
            </section>

            {/* 4. Code of Conduct */}
            <section>
              <SubtitleText>
                4. Code of Conduct
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  4.1 To engage with us and any other parties using or viewing the Application in a polite and
                  respectful manner. If you choose to use offensive, discriminatory, illegal, homophobic, racist,
                  hate speech, or otherwise unacceptable language, actions, or other communications, we may,
                  in our absolute discretion, serve you with a warning notice and/or deactivate, delete or
                  suspend your subscription and access to the Application for any period or indefinitely. We
                  reserve the right to report any illegal or unlawful activity concerning the Application to the
                  appropriate authorities.
                </p>
                <p>
                  4.2 To ensure that any information provided by you to us is accurate, truthful, not misleading, not
                  deceptive or otherwise incorrect.
                </p>
                <p>
                  4.3 Not to do anything or hold yourself out to be related in some way to us without our prior
                  written consent.
                </p>
              </div>
            </section>

            {/* 5. Contact */}
            <section>
              <SubtitleText>
                5. Contact
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  5.1 That we may contact you by any contact details you provide to us, including email, SMS,
                  telephone, at our discretion, for any reason determined by us, including, but not limited to
                  marketing of the Services, the Application or affiliate/advertiser content.
                </p>
              </div>
            </section>

            {/* 6. Advertising */}
            <section>
              <SubtitleText>
                6. Advertising
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  6.1 To advertising content being published on the Application and such content being broadcast
                  to you, without further consent required, during your use of the Application. Any
                  advertising content will consist of links to external sources which contain content of third
                  parties (<b>Third Party Content</b>). You expressly acknowledge that we do not endorse,
                  recommend or approve, or warrant the truth, accuracy, currency or suitability of, any Third
                  Party Content whether available on the Application or otherwise. You should satisfy yourself
                  as to the veracity, currency and suitability or otherwise of any Third Party Content. You
                  expressly acknowledge that you rely on Third Party Content entirely at your own risk and that
                  we have no liability to you arising in any way out of Third Party Content.
                </p>
              </div>
            </section>

            {/* 7. Marketing and Privacy Policy */}
            <section>
              <SubtitleText>
                7. Marketing and Privacy Policy
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  7.1 To consent to us using your personal information for our own purposes, including marketing
                  of the Application.
                </p>
                <p>
                  7.2 To our privacy policy which contains information on how we deal with your and any patient's
                  personal, sensitive and/or health information and how you may elect to opt out of marketing
                  communications.
                </p>
              </div>
            </section>

            {/* 8. Uploading Content */}
            <section>
              <SubtitleText>
                8. Uploading Content
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  8.1 That you represent and warrant in relation to any content you upload to the Application
                  (other than any personal, sensitive and/or health information) that you provide us with a
                  perpetual, non-exclusive, unlimited, royalty-free, irrevocable, worldwide and transferable
                  right and licence to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) reproduce;</li>
                  <li>(b) sublicense;</li>
                  <li>(c) or otherwise re-distribute,</li>
                </ul>
                <p>
                  such content in any way that we determine in our absolute discretion.
                </p>
                <p>
                  8.2 That you consent to any act or omission which would otherwise constitute an infringement by
                  us of your moral rights, and if you add any content in which any third party has moral rights,
                  you must also ensure that the third party also consents in the same manner.
                </p>
                <p>
                  8.3 That, in relation to any content (other than any personal, sensitive and/or heath information)
                  you upload to the Application:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) it is not defamatory or a malicious falsehood in relation to any product, service, person
                      or corporation;</li>
                  <li>(b) it is not the 'passing off' of any product or service and does not constitute unfair
                      competition;</li>
                  <li>(c) you own the copyright in it and it does not infringe any intellectual property right
                      including, but not limited to:</li>
                </ul>
                <ul className="list-disc pl-12 space-y-2">
                  <li>(i) trade marks (whether registered or unregistered);</li>
                  <li>(ii) business names (whether registered or unregistered),</li>
                  <li>(iii) confidential information; and</li>
                  <li>(iv) copyright;</li>
                </ul>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(d) it does not infringe any legislation or regulations of the Commonwealth of Australia and
                      the State of Queensland including, but not limited to, the:</li>
                </ul>
                <ul className="list-disc pl-12 space-y-2">
                  <li>(i) <em>Competition and Consumer Act 2010</em> (Cth);</li>
                  <li>(ii) <em>Fair Trading Act 1989</em> (Qld)</li>
                  <li>(iii) <em>Copyright Act 1968</em> (Cth);</li>
                  <li>(iv) <em>Trade Marks Act 1995</em> (Cth);</li>
                </ul>
                <p>
                  and equivalent state and territory legislation and any other legislative body competent
                  to legislate in relation to the Application or any law in any country where it is or will be
                  available electronically to users of the Application.
                </p>
                <p>
                  8.4 That, in relation to any personal, sensitive and/or health information of an individual you
                  upload to the Application:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) you have the prior written consent of the individual to upload the information to the
                      Application; and</li>
                  <li>(b) you do not infringe any provision of the <em>Privacy Act 1988</em> (Cth) or the <em>Australian Privacy
                      Principles</em>.</li>
                </ul>
              </div>
            </section>

            {/* 9. Lawful Use */}
            <section>
              <SubtitleText>
                9. Lawful Use
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  9.1 That you shall conform with all relevant laws in respect of your use of the Application.
                </p>
                <p>
                  9.2 That you shall not use the Application and the facilities contained in it in any unlawful way or
                  for any illegal purpose or in any manner that infringes our rights.
                </p>
              </div>
            </section>

            {/* 10. Application Disclaimer and Risk */}
            <section>
              <SubtitleText>
                10. Application Disclaimer and Risk
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  10.1 That we do not guarantee that:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) the Application or the Services will be secure or available at any particular time or
                      location;</li>
                  <li>(b) any defects or errors in the Application or the Services will be corrected;</li>
                  <li>(c) the Application or the Services will be free of viruses or other harmful materials;</li>
                  <li>(d) the activities of other users of the Application or the Services will be moderated,
                      controlled or authorised by us in posting content to the Application;</li>
                  <li>(e) dietary or meal plans created via the Application will be effective in improving the
                      health or fitness of any individual or patient; or</li>
                  <li>(f) content on the Application complies with the laws of any country outside of Australia.</li>
                </ul>
                <p>
                  10.2 That you use the Application solely at your own risk.
                </p>
              </div>
            </section>

            {/* 11. Suspension and Termination of Application */}
            <section>
              <SubtitleText>
                11. Suspension and Termination of Application
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  11.1 That we may at any time and without prior notice suspend the Application or disconnect or
                  deny you access to any part of it during any technical failure or maintenance period that we
                  decide to utilise.
                </p>
                <p>
                  11.2 That we may at any time without prior notice and without a reason delete specific content or
                  cease operation of the Application.
                </p>
                <p>
                  11.3 That we are not liable for any loss, damage or other consequences suffered by you as a result
                  of us suspending or ceasing operation of the Application.
                </p>
              </div>
            </section>

            {/* 12. Subscription Credentials */}
            <section>
              <SubtitleText>
                12. Subscription Credentials
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  12.1 That should you share your subscription or login details with any other person, that person is
                  subject to these T&Cs. We are not responsible for any loss, damage, or other consequences,
                  whether financial or otherwise, from the unlawful use of the Application by a party you have
                  provided with access to your account.
                </p>
                <p>
                  12.2 To secure your password. We are not responsible for any loss, damage, or other
                  consequences, whether financial or otherwise from the authorised or unauthorised use of
                  your account by any other party, regardless of whether access to your account has been
                  facilitated as a result of failing to secure your password to the Application, or any other third
                  party Application, or through other means or actions by a third party which compromises your
                  account.
                </p>
                <p>
                  12.3 That you shall keep your subscription and contact details up to date at all times through the
                  Application.
                </p>
              </div>
            </section>

            {/* Section B - Subscription */}
            <section>
              <SubtitleText>
                B. Subscription
              </SubtitleText>
            </section>

            {/* 13. Subscription Payments */}
            <section>
              <SubtitleText>
                13. Subscription Payments
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  13.1 As a Subscriber you must pay the Subscription Fee for the Services.
                </p>
                <p>
                  13.2 The Subscription Fee is payable on the applicable recurring dates. While no Subscription Fee is
                  currently charged, the Subscriber acknowledges that a fee may be introduced in the future.
                  Any such fee will be communicated in advance and charged from the first day of the calendar
                  month following its introduction.
                </p>
                <p>
                  13.3 Payments for subscriptions must be made using the payment providers specified by us.
                </p>
                <p>
                  13.4 All payments made through the Application are subject to the terms and conditions of the
                  payment providers.
                </p>
                <p>
                  13.5 We are not liable for any loss or damage suffered by you in respect of your use of payment
                  providers.
                </p>
                <p>
                  13.6 We reserve the right to charge you a fee if any payment (or purported payment) by you in
                  respect of a Subscription Fee is rejected by a payment provider, which will comprise an
                  administrative fee of $10.00 and any amount charged by the payment provider to us as a
                  result of your payment default.
                </p>
                <p>
                  13.7 You indemnify us against any loss we incur as a result of you not paying the Subscription Fee
                  as and when required, including all debt recovery and legal fees we reasonably incur in
                  recovering or attempting to recover any payments to which we are entitled under these T&Cs.
                </p>
                <p>
                  13.8 You acknowledge that a failure to make any payment in respect of the Subscription Fee will
                  result in you immediately losing access to the Application.
                </p>
              </div>
            </section>

            {/* 14. Termination */}
            <section>
              <SubtitleText>
                14. Termination
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  14.1 As a Subscriber you may terminate your subscription at any time with one month's notice to
                  us in writing.
                </p>
                <p>
                  14.2 Upon termination of your subscription:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) you acknowledge you will lose access to the Application, the Services and any
                      information stored on the Application;</li>
                  <li>(b) you must not infringe any of our intellectual property rights in the Application or any
                      dietary or meal plan;</li>
                  <li>(c) you must not use or store, and if you have stored then you must immediately delete,
                      any personal, sensitive or health information of any patient whose information you
                      have obtained from the Application, unless you have the prior written consent of the
                      patient to use or store that information;</li>
                  <li>(d) you must not do any activity that would constitute a breach of clauses 2, 3 or 4 of these
                      T&Cs.</li>
                </ul>
                <p>
                  14.3 This clause 14 as well as clauses 2, 3, 4, 15 and 16 survive termination of your subscription.
                </p>
              </div>
            </section>

            {/* Section C - Liability, Indemnity and Release */}
            <section>
              <SubtitleText>
                C. Liability, Indemnity and Release
              </SubtitleText>
            </section>

            {/* 15. Limitation of Liability */}
            <section>
              <SubtitleText>
                15. Limitation of Liability
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  15.1 To the fullest extent permitted by law, we shall not be liable to you for any lost profits or
                  revenues, or for any consequential, incidental, indirect, special, or punitive damages arising
                  out of or in connection with the Application, these T&Cs and the relationship between you
                  and us. Subject to any statutory guarantees which cannot be contracted out of (including
                  those under the <em>Australian Consumer Law</em>), in no event shall our aggregate liability for any
                  damages exceed the greater of $100.00 and the amount you paid us in the past 12 months.
                </p>
                <p>
                  15.2 In any event, you agree we shall not be responsible or liable for any loss of opportunity
                  consequential loss, loss of income or loss of profit you suffer arising from your use of the
                  Application or any dietary or meal plan.
                </p>
                <p>
                  15.3 This clause 15 survives beyond you ceasing to use the Application and the termination of
                  these T&Cs for whatever reason.
                </p>
              </div>
            </section>

            {/* 16. Indemnity and Release */}
            <section>
              <SubtitleText>
                16. Indemnity and Release
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  16.1 By using the Application, you indemnify us and our respective officers, employees and agents
                  against any claim or proceeding that is made, threatened or commenced, and any liability,
                  loss, damage or expense (including reasonable legal costs on a full indemnity basis) that we
                  incur or suffer as a direct or indirect result of:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) a breach by you of these T&Cs;</li>
                  <li>(b) an actual or alleged breach by you of any law, legislation, regulation, by-law or code of
                      conduct caused by data uploaded or downloaded from the Application; and</li>
                  <li>(c) any claims brought by or on behalf of any third party relating to any act or omission by
                      you, including breach of a third party's copyright or trade mark or misuse of an
                      individual's personal, sensitive and/or health information.</li>
                </ul>
                <p>
                  16.2 If you are a medical practitioner:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) you acknowledge that we make no warranty or guarantee as to the suitability or
                      effectiveness of any dietary or meal plan created via the Application in improving the
                      health of a patient;</li>
                  <li>(b) you acknowledge that it is the responsibility of the dietitian to whom you have referred
                      a patient to ensure any dietary or meal plan created on the Application is appropriate
                      and safe for the patient before the plan or program is implemented and to modify any
                      such plan where necessary; and</li>
                  <li>(c) to the fullest extent permitted by law, you release us from any and all liability in respect
                      of any adverse impact on a patient's health of a dietary or meal plan created via the
                      Application.</li>
                </ul>
                <p>
                  16.3 If you are a Subscriber:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) you acknowledge that we make no warranty or guarantee as to the suitability or
                      effectiveness of any dietary or meal plan created via the Application in improving the
                      health of a patient;</li>
                  <li>(b) you acknowledge that it is your responsibility to ensure any dietary or meal plan
                      created on the Application is appropriate and safe for a patient before the plan or
                      program is implemented and to modify any such plan where necessary; and</li>
                  <li>(c) to the fullest extent permitted by law, you release us from any liability in respect of any
                      adverse impact on a patient's health of a dietary or meal plan created via the
                      Application.</li>
                </ul>
                <p>
                  16.4 If you are a patient:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) you acknowledge that we make no warranty or guarantee as to the suitability or
                      effectiveness of any dietary or meal plan created via the Application in improving your
                      health;</li>
                  <li>(b) you acknowledge that it is your dietitian's responsibility to ensure any dietary or meal
                      plan created on the Application is appropriate and safe for you before the plan or
                      program is implemented and to modify any such plan where necessary; and</li>
                  <li>(c) to the fullest extent permitted by law, you release us from any liability in respect of any
                      adverse impact on your health of a dietary or meal plan created via the Application.</li>
                </ul>
                <p>
                  16.5 This clause 16 survives beyond you ceasing to use the Application and the termination of
                  these T&Cs for whatever reason.
                </p>
              </div>
            </section>

            {/* Section D - General */}
            <section>
              <SubtitleText>
                D. General
              </SubtitleText>
            </section>

            {/* 17. Definitions */}
            <section>
              <SubtitleText>
                17. Definitions
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  17.1 In these T&Cs:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) <b>Australian Consumer Law</b> means the law set out in in schedule 2 of the <em>Competition
                      and Consumer Act 2010</em> (Cth).</li>
                  <li>(b) <b>dietary or meal plan</b> means a plan created via the Application for use by a Subscriber in
                      addressing the health concerns of a patient.</li>
                  <li>(c) <b>Services</b> means:</li>
                </ul>
                <ul className="list-disc pl-12 space-y-2">
                  <li>(i) displaying and making available for acceptance referrals from medical
                      practitioners in respect of patients in need of dietitians; and</li>
                  <li>(ii) the provision of artificial intelligence generated dietary and meal plans.</li>
                </ul>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(d) <b>Subscriber</b> means a dietitian who subscribes for the Services via the Application.</li>
                  <li>(e) <b>Subscription Fee</b> means the amount payable for a Subscriber's subscription to the
                      Services, as specified in the [to be populated with definition of whatever is applicable
                      – invoice/contract/order form/subscription form]. Subscription Fee means the
                      amount payable for a Subscriber's access to the Services, as notified to the Subscriber
                      and specified in the invoice for the subscription plan as determined by the Provider.</li>
                  <li>(f) the terms <b>personal information</b>, <b>sensitive information</b>, <b>health information</b> and
                      <b> Australian Privacy Principles</b> have the meanings given to them in the <em>Privacy Act 1988</em>
                      (Cth).</li>
                </ul>
              </div>
            </section>

            {/* 18. Interpretation */}
            <section>
              <SubtitleText>
                18. Interpretation
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  18.1 In the interpretation of these T&Cs:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>(a) singular includes plural and vice versa;</li>
                  <li>(b) any gender includes every gender;</li>
                  <li>(c) a reference to a person includes corporations, trusts, associations, partnerships, a
                      government agency, and other legal entities, and where necessary, include successor
                      bodies;</li>
                  <li>(d) references to statutes include statutes amending, consolidating or replacing the
                      statutes referred to and all regulations, orders-in-council, rules, by-laws and ordinances
                      made under those statutes;</li>
                  <li>(e) headings are used for convenience only and are to be disregarded in the interpretation
                      of these T&Cs;</li>
                  <li>(f) where any word or phrase is given a defined meaning, another grammatical form of
                      that word or phrase has a corresponding meaning; and</li>
                  <li>(g) each paragraph or sub-paragraph in a list is to be read independently from the others in
                      the list.</li>
                </ul>
              </div>
            </section>

            {/* 19. Jurisdiction */}
            <section>
              <SubtitleText>
                19. Jurisdiction
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  19.1 These T&Cs are subject to the laws of the State of Queensland, Australia and each party
                  submits to the non-exclusive authority of the Courts of that jurisdiction.
                </p>
              </div>
            </section>

            {/* 20. Severability */}
            <section>
              <SubtitleText>
                20. Severability
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  20.1 If a provision of these T&Cs is held invalid or unenforceable by any Court such determination
                  and consequential severance (if any) shall not invalidate the rest of these T&Cs, which shall
                  remain in full force and effect as if such terms and provisions had not been made a part of it.
                </p>
              </div>
            </section>

            {/* 21. Entire Agreement */}
            <section>
              <SubtitleText>
                21. Entire Agreement
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  21.1 These T&Cs and any warranties implied by law which are not capable of being excluded or
                  modified embody the entire agreement between us and you. Any contact by you with
                  customer service officers of ours that includes any negotiations, representations, warranties,
                  arrangements and statements (if any) whether expressed or implied, including any collateral
                  agreement or warranty, with reference to the subject matter or the intentions of either of the
                  parties are merged and otherwise are excluded and cancelled by these T&Cs.
                </p>
              </div>
            </section>

            {/* 22. Waiver */}
            <section>
              <SubtitleText>
                22. Waiver
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  22.1 If we do not act in relation to a particular breach by you of these T&Cs this will not be treated
                  as a waiver by us of our right to act with respect to subsequent or similar breaches.
                </p>
                <p>
                  22.2 For the avoidance of doubt, a waiver by us is only of effect if in writing.
                </p>
              </div>
            </section>

            {/* 23. Our Contact Details */}
            <section>
              <SubtitleText>
                23. Our Contact Details
              </SubtitleText>
              <div className={cn(
                "space-y-4",
                isDark ? "text-secondary-300" : "text-secondary-700"
              )}>
                <p>
                  23.1 If you have any questions about these Terms & Conditions please contact us using the
                  following details:
                </p>
                <div className={cn(
                  "p-4 rounded-lg",
                  isDark ? "bg-secondary-800" : "bg-secondary-50"
                )}>
                  <p>Biz Group Pty Ltd ACN 671 345 181 trading as BiteWise Medical</p>
                  <p>Address: Level 34, 1 Eagle Street, Brisbane, QLD, 4000.</p>
                  <p>Phone: 0422 395 883</p>
                  <p>Email: support@bitewise.com.au</p>
                </div>
              </div>
            </section>
          </Card>
        </div>
      </div>
    </Layout>
  )
}

export { TermsAndConditionsPage }